import React, { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import {
  StyleSheet,
  View,
  Pressable,
  type TextStyle,
  type ViewStyle,
  Platform,
  type AccessibilityProps,
  type AccessibilityRole,
} from "react-native";

import { useI18n } from "../../shared/I18n";
import { escapeRegExp } from "../../shared/util/escapeRegExp";
import type { IconProps } from "../icons/Icon";
import { List, type ListAccessibilityLabelProps } from "../List";
import { LiveRegion } from "../LiveRegion";
import { Text } from "../Text";
import { TextInput, type TextInputProps } from "../TextInput";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { VisuallyHidden } from "../VisuallyHidden";

export type SuggestListItemVariant = "default" | "currentLocation";

export interface SuggestListItemProps {
  /**
   * Stable unique identifier for this item. Used as the React key.
   * Falls back to `text` if not provided.
   */
  id?: string;

  /**
   * The text content of the list item
   */
  text: string;

  /**
   * Optional description text displayed below the main text
   */
  description?: string;

  /**
   * Optional icon to display before the text
   */
  icon?: ReactNode;

  /**
   * Callback function when the list item is pressed
   */
  onPress?: () => void;

  /**
   * Whether the list item is disabled
   */
  isDisabled?: boolean;

  /**
   * The variant of the list item
   * @default 'default'
   */
  variant?: SuggestListItemVariant;

  /**
   * Optional term to match against the text. Non-matching parts will be bold.
   */
  matchTerm?: string;

  /**
   * Whether the list item is currently focused via keyboard navigation.
   * When true, applies a highlight background color.
   */
  isFocused?: boolean;

  /**
   * Optional. Used to set aria-selected.
   */
  isSelected?: boolean;

  /**
   * Optional native ID for the list item element.
   * Maps to `id` on web. Used for aria-activedescendant support.
   */
  nativeID?: string;

  /**
   * When true, renders the item as role="option" on web (for combobox/listbox patterns).
   * On native, has no effect.
   */
  isComboboxOption?: boolean;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

/**
 * A list item component that can display an optional icon and text, following MADS.
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=10217-10370
 */
export function SuggestListItem({
  text,
  description,
  icon,
  onPress,
  isDisabled = false,
  variant = "default",
  matchTerm,
  isFocused,
  isSelected = false,
  nativeID,
  isComboboxOption = false,
  sx,
}: SuggestListItemProps) {
  const theme = useTheme();

  const styles = StyleSheet.create({
    container: {
      display: "flex",
      flexDirection: "row",
      alignItems: description ? "flex-start" : "center",
      paddingVertical: theme.spacing.s2,
      paddingHorizontal: theme.spacing.s3,
    },
    content: {
      flex: 1,
      flexDirection: "row",
      alignItems: description ? "flex-start" : "center",
    },
    iconContainer: {
      marginRight: theme.spacing.s2,
    },
    textContainer: {
      flex: 1,
    },
    list: {
      display: "flex",
      flexDirection: "column",
    },
  });

  const textColor =
    variant === "currentLocation"
      ? theme.colors.foregroundActionPrimary
      : theme.colors.foregroundStrong;

  const styledIcon = icon
    ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      React.cloneElement(icon as React.ReactElement<IconProps & AccessibilityProps>, {
        color: textColor,
        "aria-hidden": true,
        importantForAccessibility: "no-hide-descendants",
        accessibilityElementsHidden: true,
      })
    : null;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const bold = String(theme.fontWeights.bold) as TextStyle["fontWeight"];
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const normal = String(theme.fontWeights.normal) as TextStyle["fontWeight"];

  const renderText = () => {
    if (!matchTerm || variant === "currentLocation") {
      return (
        <Text
          level={3}
          sx={{
            color: textColor,
            fontWeight: bold,
          }}
        >
          {text}
        </Text>
      );
    }

    const escapedMatchTerm = escapeRegExp(matchTerm);
    const parts = text.split(new RegExp(`(${escapedMatchTerm})`, "gi"));
    return (
      <Text level={3}>
        {parts
          .filter((part) => part !== "")
          .map((part, index) => (
            <Text
              key={index}
              sx={{
                color: textColor,
                fontWeight: part.toLowerCase() === matchTerm.toLowerCase() ? normal : bold,
              }}
            >
              {part}
            </Text>
          ))}
      </Text>
    );
  };

  return (
    <Pressable
      accessibilityRole={
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        Platform.OS === "web" && isComboboxOption ? ("option" as AccessibilityRole) : "button"
      }
      onPress={onPress}
      disabled={isDisabled}
      focusable={!isDisabled}
      nativeID={nativeID}
      accessibilityState={{ selected: isSelected, disabled: isDisabled }}
      aria-selected={Platform.OS === "web" && isComboboxOption ? isSelected : undefined}
      style={({ pressed }) =>
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        [
          styles.container,
          sx,
          {
            backgroundColor: pressed && !isDisabled ? theme.colors.background : "transparent",
            opacity: isDisabled ? 0.4 : 1,
            // Web-only: Show custom focus indicator when keyboard focused
            ...(Platform.OS === "web"
              ? {
                  outlineStyle: isFocused ? "solid" : "none",
                  outlineWidth: isFocused ? 2 : undefined,
                  outlineColor: isFocused ? theme.colors.utilityFocus : undefined,
                  outlineOffset: isFocused ? -2 : undefined,
                  borderRadius: isFocused ? theme.borderRadius.md : undefined,
                }
              : {}),
          },
        ] as unknown as ViewStyle
      }
    >
      <View style={styles.content}>
        {styledIcon && (
          <View testID={"suggest-list-icon"} style={styles.iconContainer}>
            {styledIcon}
          </View>
        )}
        <View style={styles.textContainer}>
          {renderText()}
          {description && (
            <Text
              level={2}
              sx={{
                color: textColor,
              }}
            >
              {description}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

export interface SuggestListAccessibilityLabelProps extends ListAccessibilityLabelProps {
  /**
   * Text to announce when suggestions are found.
   * Use ${count} to denote the number of results.
   * @default Localized "n suggestions found"
   */
  resultsCountText?: string;
}

export interface SuggestListProps {
  /**
   * Array of items to display in the list
   */
  items: SuggestListItemProps[];

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Test ID for the list component
   */
  testID?: string;

  /**
   * Accessible labels to describe parts of the list
   */
  a11yLabels?: SuggestListAccessibilityLabelProps;

  /**
   * Optional native ID for the list element.
   * Maps to `id` on web.
   */
  nativeID?: string;

  /**
   * Optional: Props to pass to the integrated search TextInput.
   * When provided, enables integrated search behavior.
   */
  textInputProps?: TextInputProps;

  /**
   * Optional: When the keyboard is visible, ensures taps on suggestion items are delivered
   * to the list instead of dismissing the keyboard first. Use "handled" when
   * the list is below a text input so the first tap selects a suggestion.
   * @default 'handled' when textInputProps is provided, 'never' otherwise
   */
  keyboardShouldPersistTaps?: "always" | "never" | "handled";

  /**
   * Optional ref to the container View. If not provided, an internal ref is used.
   * This is used to attach keyboard event listeners to an element in the container if the internal TextInput is not used.
   */
  containerRef?: React.RefObject<View | null>;

  /**
   * Called whenever the keyboard-highlighted index changes.
   * Useful for external components that need to set aria-activedescendant
   * on a TextInput rendered outside of SuggestList.
   */
  onHighlightedIndexChange?: (index: number) => void;

  /**
   * When true, uses `FlatList` for efficient rendering, only mounting
   * visible items. Ideal for large lists or lists with complex item components.
   *
   * When false, renders all items in a plain `View` via `.map()`. Useful
   * for small lists, or when the list is nested inside a `ScrollView` that
   * needs to measure the full content height. If set to false, ensure the
   * number of items is small and finite to avoid performance issues.
   *
   * @default true
   */
  isVirtualized?: boolean;
}

function getItemKey(item: SuggestListItemProps): string {
  return item.id ?? item.text;
}

function useKeyboardNavigation({
  items,
  containerRef,
  onHighlightedIndexChange,
}: {
  items: SuggestListItemProps[];
  containerRef: React.RefObject<View | null>;
  onHighlightedIndexChange?: (index: number) => void;
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  const itemsKey = items.map(getItemKey).join("\0");
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [itemsKey]);

  useEffect(() => {
    onHighlightedIndexChange?.(highlightedIndex);
  }, [highlightedIndex, onHighlightedIndexChange]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const currentItems = itemsRef.current;
    if (!currentItems.length) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < currentItems.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : currentItems.length - 1));
        break;
      case "Enter": {
        const idx = highlightedIndexRef.current;
        if (idx >= 0 && idx < currentItems.length) {
          event.preventDefault();
          currentItems[idx].onPress?.();
          setHighlightedIndex(-1);
        }
        break;
      }
      case "Home":
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setHighlightedIndex(currentItems.length - 1);
        break;
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web" || !containerRef.current) return;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const container = containerRef.current as unknown as HTMLElement;
    const input = container.querySelector("input");
    if (!input) return;

    input.addEventListener("keydown", handleKeyDown);
    return () => input.removeEventListener("keydown", handleKeyDown);
  }, [containerRef, handleKeyDown]);

  return highlightedIndex;
}

/**
 * A list component that displays a collection of SuggestListItems.
 * Used for suggestion lists where items are not destructive or selectable.
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=10217-10370
 */
export function SuggestList({
  items = [],
  sx,
  a11yLabels,
  nativeID,
  testID,
  textInputProps,
  keyboardShouldPersistTaps,
  containerRef: externalContainerRef,
  onHighlightedIndexChange,
  isVirtualized = true,
}: SuggestListProps) {
  const theme = useTheme();
  const gt = useI18n();
  let listId = `suggest-list-${useId()}`;
  if (nativeID) {
    listId = nativeID;
  }
  const inputId = `suggest-list-input-${useId()}`;

  const styles = StyleSheet.create({
    list: {
      display: "flex",
      flexDirection: "column",
    },
    inputContainer: {
      marginBottom: theme.spacing.s3,
      paddingHorizontal: theme.spacing.s3,
    },
  });

  // Attach keyboard event listener for navigation and selection (web only).
  const internalContainerRef = useRef<View>(null);
  const containerRef = externalContainerRef ?? internalContainerRef;
  const highlightedIndex = useKeyboardNavigation({
    items,
    containerRef,
    onHighlightedIndexChange,
  });

  const getItemProps = (item: SuggestListItemProps, index: number): SuggestListItemProps => ({
    ...item,
    matchTerm: textInputProps?.value !== undefined ? textInputProps.value : item.matchTerm,
    nativeID: `${listId}-item-${index}`,
    isSelected: highlightedIndex === index,
    isFocused: highlightedIndex === index,
  });

  const listComponent =
    Platform.OS === "web" ? (
      // On web, we want to be able to use the built-in keyboard navigation
      // and selection behavior of the listbox role.
      <View
        nativeID={listId}
        style={[styles.list, !textInputProps && sx]}
        // @ts-expect-error role is not in View's props but is valid on web
        role="listbox"
      >
        {items.map((item, index) => (
          <SuggestListItem key={getItemKey(item)} {...getItemProps(item, index)} isComboboxOption />
        ))}
      </View>
    ) : (
      <List
        nativeID={listId}
        style={[styles.list, !textInputProps && sx]}
        keyboardShouldPersistTaps={
          keyboardShouldPersistTaps ?? (textInputProps ? "handled" : "never")
        }
        isVirtualized={isVirtualized}
        data={items.map((item, index) => ({
          key: getItemKey(item),
          content: <SuggestListItem {...getItemProps(item, index)} />,
        }))}
        a11yLabels={a11yLabels}
        testID={testID}
      />
    );

  if (!textInputProps) {
    return listComponent;
  }

  const announcement =
    a11yLabels?.resultsCountText?.replace("${count}", items.length.toString()) ??
    gt.ngettext(
      // # Announcement when suggestions are found
      "{0} suggestion found",
      "{0} suggestions found",
      items.length,
      [items.length.toString()],
    );

  return (
    <View ref={containerRef} style={sx}>
      <LiveRegion>
        <VisuallyHidden>{announcement}</VisuallyHidden>
      </LiveRegion>
      <View style={styles.inputContainer}>
        <TextInput
          {...textInputProps}
          testID={textInputProps.testID ?? (testID ? `${testID}-input` : "suggest-list-input")}
          nativeID={textInputProps.nativeID ?? inputId}
          accessibilityRole="combobox"
          accessibilityState={{
            expanded: items.length > 0,
            ...textInputProps.accessibilityState,
          }}
          accessibilityControls={listId}
          aria-activedescendant={
            Platform.OS === "web" && highlightedIndex >= 0
              ? `${listId}-item-${highlightedIndex}`
              : undefined
          }
        />
      </View>
      {listComponent}
    </View>
  );
}
